import type { OfflineDatabaseSqlStatement } from './offlineDatabase.statements';
import type { PhotoQueueItem } from './offlineDatabase.types';

type PhotoQueueStatus = PhotoQueueItem['status'];

export interface PhotoQueueRowWriteValues {
  environmentKey: string;
  localPath: string;
  reviewResponseId: number | null;
  tempResponseId: string | null;
  fileName: string;
  mimeType: string;
  size: number;
  caption: string | null;
  note: string | null;
  metadata: string | null;
  status: PhotoQueueStatus;
  retryCount: number;
  errorMessage: string | null;
  priority: NonNullable<PhotoQueueItem['priority']>;
  isHandwriting: number;
  presignedUrlExpiry: string | null;
  processedForVision: number;
  createdAt: string;
  lastAttemptAt: string | null;
}

export interface PhotoMetadataRowWriteValues {
  id: number;
  responseId: number;
  s3Key: string;
  s3Url: string | null;
  fileName: string;
  mimeType: string;
  size: number | null;
  caption: string | null;
  note: string | null;
  noteSyncStatus: 'pending' | 'syncing' | 'synced' | 'failed';
  noteSyncError: string | null;
  metadata: string | null;
  questionId: string | null;
  uploadedAt: string | null;
  cachedAt: string;
}

export interface PhotoQueueSelectOptions {
  status?: PhotoQueueStatus;
  environmentKey?: string;
  limit?: number;
}

export const buildPhotoQueueSelectStatement = (
  options: PhotoQueueSelectOptions = {}
): OfflineDatabaseSqlStatement => {
  let sql = 'SELECT * FROM photos_queue WHERE 1=1';
  const params: unknown[] = [];

  if (options.status) {
    sql += ' AND status = ?';
    params.push(options.status);
  }

  if (options.environmentKey) {
    sql += ' AND environment_key = ?';
    params.push(options.environmentKey);
  }

  sql += ' ORDER BY created_at ASC';

  if (options.limit) {
    sql += ' LIMIT ?';
    params.push(options.limit);
  }

  return { sql, params };
};

export const buildPhotoQueueDeleteStatement = (
  id: number
): OfflineDatabaseSqlStatement => ({
  sql: 'DELETE FROM photos_queue WHERE id = ?',
  params: [id],
});

export const buildPhotoQueueInsertStatement = (
  values: PhotoQueueRowWriteValues
): OfflineDatabaseSqlStatement => ({
	  sql: `INSERT INTO photos_queue (
	          environment_key, local_path, review_response_id, temp_response_id, file_name,
	          mime_type, size, caption, note, metadata, status, retry_count,
	          error_message, priority, is_handwriting, presigned_url_expiry,
	          processed_for_vision, created_at, last_attempt_at
	        ) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
	  params: [
	    values.environmentKey,
	    values.localPath,
    values.reviewResponseId,
    values.tempResponseId,
    values.fileName,
    values.mimeType,
    values.size,
    values.caption,
    values.note,
    values.metadata,
    values.status,
    values.retryCount,
    values.errorMessage,
    values.priority,
    values.isHandwriting,
    values.presignedUrlExpiry,
    values.processedForVision,
    values.createdAt,
    values.lastAttemptAt,
  ],
});

export const buildPhotoMetadataByResponseSelectStatement = (
  responseId: number
): OfflineDatabaseSqlStatement => ({
  sql: 'SELECT * FROM photos_metadata WHERE response_id = ? ORDER BY id ASC',
  params: [responseId],
});

export const buildPhotoMetadataDeleteStatement = (
  photoId: number
): OfflineDatabaseSqlStatement => ({
  sql: 'DELETE FROM photos_metadata WHERE id = ?',
  params: [photoId],
});

export const buildPhotoMetadataByResponseDeleteStatement = (
  responseId: number
): OfflineDatabaseSqlStatement => ({
  sql: 'DELETE FROM photos_metadata WHERE response_id = ?',
  params: [responseId],
});

export const buildPhotoMetadataUpsertStatement = (
  values: PhotoMetadataRowWriteValues
): OfflineDatabaseSqlStatement => ({
  sql: `INSERT OR REPLACE INTO photos_metadata (
          id, response_id, s3_key, s3_url, file_name, mime_type, size,
          caption, note, note_sync_status, note_sync_error, metadata,
          question_id, uploaded_at, cached_at
        ) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
  params: [
    values.id,
    values.responseId,
    values.s3Key,
    values.s3Url,
    values.fileName,
    values.mimeType,
    values.size,
    values.caption,
    values.note,
    values.noteSyncStatus,
    values.noteSyncError,
    values.metadata,
    values.questionId,
    values.uploadedAt,
    values.cachedAt,
  ],
});
